"use client";
import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { formatProfileName } from "../../utils/profileName";

export default function LeaveRequestPage() {
  const { isEnabled } = useFeatureFlags();
  const leaveSubmissionEnabled = isEnabled("feature.staff_leave_submission");
  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  });

  const [myRequests, setMyRequests] = useState([]);
  const [fetchingRequests, setFetchingRequests] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: currentUser } = await supabase.auth.getUser();
      if (currentUser?.user) {
        setUser(currentUser.user);
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name, name_prefix")
          .eq("id", currentUser.user.id)
          .single();

        if (profile) setFullName(formatProfileName(profile, ""));
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) fetchMyRequests();
  }, [user]);

  const fetchMyRequests = async () => {
    setFetchingRequests(true);
    const { data } = await supabase
      .from("leave_requests")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (data) setMyRequests(data);
    setFetchingRequests(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leaveSubmissionEnabled) {
      alert("Leave submissions are currently disabled by the developer.");
      return;
    }
    if (!form.leave_type || !form.start_date || !form.end_date) {
      alert("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      await supabase.from("leave_requests").insert([
        {
          user_id: user.id,
          full_name: fullName,
          leave_type: form.leave_type,
          start_date: form.start_date,
          end_date: form.end_date,
          reason: form.reason,
        },
      ]);
      alert("Leave request submitted successfully!");
      setForm({ leave_type: "", start_date: "", end_date: "", reason: "" });
      fetchMyRequests();
    } catch (error) {
      console.error(error);
      alert("Error submitting leave request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="w-full max-w-xl mx-auto px-6 py-8 flex-1 space-y-12">
        <section>
          <h1 className="nhsuk-heading-l mb-6">Submit Leave of Absence</h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type of LOA */}
            <div>
              <label htmlFor="leave_type" className="nhsuk-label">
                Type of LOA
              </label>
              <select
                id="leave_type"
                className="nhsuk-select"
                value={form.leave_type}
                onChange={(e) =>
                  setForm({ ...form, leave_type: e.target.value })
                }
              >
                <option value="">Select type</option>
                <option value="Short-Term">Short-Term</option>
                <option value="Long-Term">Long-Term</option>
                <option value="Unspecified">Unspecified</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label htmlFor="start_date" className="nhsuk-label">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                className="nhsuk-input"
                value={form.start_date}
                onChange={(e) =>
                  setForm({ ...form, start_date: e.target.value })
                }
              />
            </div>

            {/* End Date */}
            <div>
              <label htmlFor="end_date" className="nhsuk-label">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                className="nhsuk-input"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>

            {/* Reason */}
            <div>
              <label htmlFor="reason" className="nhsuk-label">
                Reason
              </label>
              <textarea
                id="reason"
                className="nhsuk-textarea"
                placeholder="Optional"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </div>

            <p className="text-sm text-gray-600">
              Submitting an LOA request does not guarantee approval.
            </p>

            {!leaveSubmissionEnabled && <div className="nhsuk-inset-text">New leave submissions are currently disabled. Existing requests remain visible below.</div>}
            <button type="submit" className="nhsuk-button" disabled={loading || !leaveSubmissionEnabled}>
              {loading ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </section>

        {/* ================= MY REQUESTS ================= */}
        <section>
          <h2 className="nhsuk-heading-m mb-4">My Requests</h2>

          {fetchingRequests ? (
            <p>Loading requests...</p>
          ) : myRequests.length === 0 ? (
            <p className="text-sm italic text-gray-600">
              You have not submitted any leave requests yet.
            </p>
          ) : (
            <div className="space-y-2">
              {myRequests.map((req) => (
                <div
                  key={req.id}
                  className="border rounded p-4 flex justify-between items-center"
                >
                  <div className="space-y-1 text-sm">
                    <p>
                      <strong>Type:</strong> {req.leave_type}
                    </p>
                    <p>
                      <strong>From:</strong>{" "}
                      {new Date(req.start_date).toLocaleDateString("en-GB")}
                    </p>
                    <p>
                      <strong>To:</strong>{" "}
                      {new Date(req.end_date).toLocaleDateString("en-GB")}
                    </p>
                    {req.management_created && (
                      <p><strong>Recorded by:</strong> Management</p>
                    )}
                    {req.reason && (
                      <p><strong>Reason:</strong> {req.reason}</p>
                    )}
                    {req.management_notes && (
                      <p><strong>Management note:</strong> {req.management_notes}</p>
                    )}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-bold ${
                      req.status === "Approved"
                        ? "bg-green-600 text-white"
                        : req.status === "Rejected"
                        ? "bg-red-600 text-white"
                        : "bg-yellow-400 text-black"
                    }`}
                  >
                    {req.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
